import { ComponentPropsWithoutRef, createContext, useContext, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box } from '@givebutter/primitives/box';
import { Button, ButtonProps, ButtonVariantType } from '@givebutter/primitives/button';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CopyButton } from 'components/copy-button';
import { inlineAddress } from 'helpers/address';
import { twMerge } from 'tailwind-merge';
import { Address } from 'types/domain';

const AddressManagementRoot = (props: ComponentPropsWithoutRef<'div'>) => {
    const { children, className = '' } = props;

    return <Box className={twMerge('flex flex-col gap-4', className)}>{children}</Box>;
};

const AddressManagementTitle = (props: ComponentPropsWithoutRef<'div'>) => {
    const { children, className = '' } = props;

    return <h2 className={twMerge('text-lg font-medium', className)}>{children ?? 'Addresses'}</h2>;
};

interface AddressManagementButtonProps extends Omit<ButtonProps, 'variant'> {
    variant?: ButtonVariantType;
}
const AddressManagementButton = (props: AddressManagementButtonProps) => {
    const {
        children,
        className = '',
        onClick,
        size = 'sm',
        variant = 'outline',
        color = 'neutral',
    } = props;
    const history = useHistory();
    const match = useRouteMatch();

    return (
        <Button
            onClick={e => (onClick ? onClick(e) : history.push(`${match.url}/new`))}
            className={twMerge('flex flex-nowrap items-center gap-2', className)}
            size={size}
            variant={variant}
            color={color}
        >
            {children ?? (
                <>
                    <FontAwesomeIcon icon={faPlus} />
                    <p>Add</p>
                </>
            )}
        </Button>
    );
};

const AddressManagementRow = (props: ComponentPropsWithoutRef<'div'>) => {
    const { children, className = '' } = props;

    return (
        <div className={twMerge('flex items-center justify-between', className)}>{children}</div>
    );
};

const AddressManagementRowDetails = (props: ComponentPropsWithoutRef<'div'>) => {
    const { children, className = '' } = props;

    return <div className={twMerge('flex flex-col', className)}>{children}</div>;
};

interface AddressManagementInlineAddressContext {
    address: Address;
}

const AddressManagementInlineAddressContext = createContext<AddressManagementInlineAddressContext>(
    {} as AddressManagementInlineAddressContext,
);

interface AddressManagementInlineAddressRootProps extends ComponentPropsWithoutRef<'div'> {
    address: Address;
}

const AddressManagementInlineAddressRoot = (props: AddressManagementInlineAddressRootProps) => {
    const { address, children, className = '' } = props;
    const value = useMemo(() => ({ address }), [address]);

    return (
        <AddressManagementInlineAddressContext.Provider value={value}>
            <div className={twMerge('flex items-center gap-2', className)}>{children}</div>
        </AddressManagementInlineAddressContext.Provider>
    );
};

const AddressManagementInlineAddressCopyButton = (props: ComponentPropsWithoutRef<'div'>) => {
    const { children, className = '' } = props;
    const { address } = useContext(AddressManagementInlineAddressContext);
    const inlinedAddress = inlineAddress(address);

    return (
        <div className={twMerge('flex items-center', className)}>
            {children}
            <p className={'text-base'}>{inlinedAddress}</p>
            <CopyButton text={inlinedAddress} />
        </div>
    );
};

const Root = AddressManagementRoot;
const Title = AddressManagementTitle;
const Row = AddressManagementRow;
const RowDetails = AddressManagementRowDetails;
const InlineAddressRoot = AddressManagementInlineAddressRoot;
const InlineAddressCopyButton = AddressManagementInlineAddressCopyButton;

export {
    Root,
    Title,
    AddressManagementButton as Button,
    Row,
    RowDetails,
    InlineAddressRoot,
    InlineAddressCopyButton,
    //
    AddressManagementRoot,
    AddressManagementTitle,
    AddressManagementButton,
    AddressManagementRow,
    AddressManagementRowDetails,
    AddressManagementInlineAddressRoot,
    AddressManagementInlineAddressCopyButton,
};
